/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useSelector, useDispatch } from "react-redux";
import { useMemo } from "react";
import {
  setSelectedLocation,
  setSelectedServices,
  addSelectedService,
  removeSelectedService,
  setSelectedProvider,
  setSelectedCategories,
  addSelectedCategory,
  removeSelectedCategory,
  setSelectedDate,
  setSelectedTime,
  resetSelectedDate,
  updateBooking,
  setBookingResponse,
  setInvoice,
  resetBookingRequest,
  resetBookingState,
  setBookingLoading,
  setBookingError,
  clearBookingError,
  setSchedulerChannel,
  resetSchedulerChannel,
  setPromotionCode,
  clearPromotionCode,
  setScrollToServiceId,
  clearScrollToServiceId,
  setSelectedProducts,
  addDefaultAddons,
  toggleAddon,
  markExclusiveAddonNoteShown,
  updateAddonQty,
  addDefaultProducts,
  toggleProduct,
  updateProductQty,
} from "../store/slices/bookingSlice";
import { useFilterAllowedServices } from "./useFilterAllowedServices";
import { useFilterAllowedProviders } from "./useFilterAllowedProviders";
import { useFilterAllowedProducts } from "./useFilterAllowedProducts";
import { useAnyUnit } from "../lib/react-query/useAnyUnit";
import usePluginUtils from "./usePluginUtils";

export function useBooking() {
  const dispatch = useDispatch();
  const { isPluginEnabled } = usePluginUtils();
  const { data: anyUnit } = useAnyUnit({
    enabled: isPluginEnabled("any_unit"),
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const bookingState = useSelector((state) => state.booking);
  const {
    selectedLocation,
    selectedServices,
    selectedProvider: rawSelectedProvider,
    selectedCategories,
    selectedProducts,
    selectedDate,
    selectedTime,
    timeValidatedProducts,
    invoice,
    bookingRequest,
    bookingResponse,
    isBookingLoading,
    bookingError,
    schedulerChannel,
    promotionCode,
    membershipAccessGrants,
    pendingAccessService,
    scrollToServiceId,
    isExclusiveAddonNoteShown,
  } = bookingState;

  // Memoized selected provider (handles anyUnit logic)
  const selectedProvider = useMemo(() => {
    if (anyUnit?.hide_other_providers) {
      return anyUnit;
    }
    return rawSelectedProvider;
  }, [anyUnit, rawSelectedProvider]);

  // Filter hooks
  const filterAllowedServices = useFilterAllowedServices(selectedProvider, selectedServices);
  const filterAllowedProviders = useFilterAllowedProviders(selectedServices, anyUnit);
  const filterAllowedProducts = useFilterAllowedProducts(selectedServices, selectedProducts);

  // Action creators
  const actions = useMemo(
    () => ({
      // Location actions
      setSelectedLocation: (location) => dispatch(setSelectedLocation(location)),

      // Services actions
      setSelectedServices: (services) => dispatch(setSelectedServices(services)),
      addSelectedService: (service) => dispatch(addSelectedService(service)),
      removeSelectedService: (serviceId) => dispatch(removeSelectedService(serviceId)),

      // Provider actions
      setSelectedProvider: (provider) => dispatch(setSelectedProvider(provider)),

      // Categories actions
      setSelectedCategories: (categories) => dispatch(setSelectedCategories(categories)),
      addSelectedCategory: (category) => dispatch(addSelectedCategory(category)),
      removeSelectedCategory: (categoryId) => dispatch(removeSelectedCategory(categoryId)),

      // Addons actions
      handleAddonSelect: (serviceId, addon, exclusive = false) => dispatch(toggleAddon({serviceId, addon, exclusive})),
      updateAddon: (serviceId, addon, qty) => dispatch(updateAddonQty({serviceId, addon, qty})),
      addDefaultAddons: (serviceId, addons) => dispatch(addDefaultAddons({serviceId, addons})),
      markExclusiveAddonNoteShown: () => dispatch(markExclusiveAddonNoteShown()),

      // Products actions
      setSelectedProducts: (products) => dispatch(setSelectedProducts(products)),
      handleProductSelect: (serviceId, product) => dispatch(toggleProduct({serviceId, product})),
      updateProduct: (serviceId, product, qty) => dispatch(updateProductQty({serviceId, product, qty})),
      addDefaultProducts: (serviceId, products) => dispatch(addDefaultProducts({serviceId, products})),

      // Date and time actions
      setSelectedDate: (date) => dispatch(setSelectedDate(date)),
      setSelectedTime: (time) => dispatch(setSelectedTime(time)),
      resetSelectedDate: () => dispatch(resetSelectedDate()),

      // Manual booking request updates - for custom data
      updateBooking: (data) => dispatch(updateBooking(data)),

      // Booking response actions
      setBookingResponse: (response) => dispatch(setBookingResponse(response)),

      // Invoice actions
      setInvoice: (invoiceData) => dispatch(setInvoice(invoiceData)),

      // Reset actions
      resetBookingRequest: () => dispatch(resetBookingRequest()),
      resetBookingState: () => dispatch(resetBookingState()),

      // UI state actions (for local state only, React Query handles API loading states)
      setBookingLoading: (loading) => dispatch(setBookingLoading(loading)),
      setBookingError: (error) => dispatch(setBookingError(error)),
      clearBookingError: () => dispatch(clearBookingError()),

      // Scheduler channel actions
      setSchedulerChannel: (channel) => dispatch(setSchedulerChannel(channel)),
      resetSchedulerChannel: () => dispatch(resetSchedulerChannel()),

      // Promotion code actions
      setPromotionCode: (code) => dispatch(setPromotionCode(code)),
      clearPromotionCode: () => dispatch(clearPromotionCode()),

      // One-shot "scroll to preselected service" target (Services page)
      setScrollToServiceId: (serviceId) => dispatch(setScrollToServiceId(serviceId)),
      clearScrollToServiceId: () => dispatch(clearScrollToServiceId()),
    }),
    [dispatch]
  );

  return {
    // Basic state
    selectedLocation,
    selectedServices,
    selectedProvider,
    selectedCategories,
    selectedProducts,
    selectedDate,
    selectedTime,
    timeValidatedProducts,
    invoice,
    bookingRequest,
    bookingResponse,
    isBookingLoading,
    bookingError,
    schedulerChannel,
    promotionCode,
    membershipAccessGrants,
    pendingAccessService,
    scrollToServiceId,
    isExclusiveAddonNoteShown,
    // Actions
    ...actions,

    // Filter functions
    filterAllowedServices,
    filterAllowedProviders,
    filterAllowedProducts,
  };
}
